import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { teal, tealDark } from "@air/colors";
import isUndefined from "lodash/isUndefined";
import { useTheme } from "next-themes";
import { memo, useCallback, useMemo, useState } from "react";
import { AnnotationCommentInput } from "~/components/Annotations/shared/AnnotationCommentInput";
import { getAnnotationCommentPos, getPageNumber } from "~/components/Annotations/shared/annotationUtils";
import { BoundingBoxLayer } from "~/components/BoundingBox/components/BoundingBoxLayer";
import { drawClearedBoundingBoxOnCanvas } from "~/components/BoundingBox/utils/drawBoxesOnCanvas";
import { QueryParamNames } from "~/constants/search";
import { setQueryParams } from "~/utils/PathUtils";
const BORDER_WIDTH = 2;
export const BoxAnnotationLayer = /*#__PURE__*/ memo((param)=>{
    let { disabled, containerRef, pageNumber, onIsSelectingChange, containerWidth, containerHeight, activeAnnotation, onAnnotationDrawn, onClear, newAnnotation, shouldDrawOverlay, containerTop, containerLeft } = param;
    const theme = useTheme();
    const strokeColor = theme.theme === "dark" ? tealDark.teal7 : teal.teal7;
    const annotationToDraw = activeAnnotation || newAnnotation;
    const [isDrawingActive, setIsDrawingActive] = useState(false);
    const _onIsSelectingChange = useCallback((isSelecting)=>{
        onIsSelectingChange === null || onIsSelectingChange === void 0 ? void 0 : onIsSelectingChange(isSelecting);
        setIsDrawingActive(isSelecting);
    }, [
        onIsSelectingChange
    ]);
    const drawNewAnnotation = useCallback((param)=>{
        let { canvas, rect } = param;
        return drawClearedBoundingBoxOnCanvas({
            canvas,
            box: rect,
            strokeColor,
            borderWidth: BORDER_WIDTH
        });
    }, [
        strokeColor
    ]);
    // Give clicked annotation boxes prominence.
    const annotationBoxToDraw = useMemo(()=>{
        if (!annotationToDraw) {
            return undefined;
        }
        return {
            pctEndX: (annotationToDraw === null || annotationToDraw === void 0 ? void 0 : annotationToDraw.dimensions.width) || 0,
            pctEndY: (annotationToDraw === null || annotationToDraw === void 0 ? void 0 : annotationToDraw.dimensions.height) || 0,
            pctStartX: (annotationToDraw === null || annotationToDraw === void 0 ? void 0 : annotationToDraw.coordinates.x) || 0,
            pctStartY: (annotationToDraw === null || annotationToDraw === void 0 ? void 0 : annotationToDraw.coordinates.y) || 0
        };
    }, [
        annotationToDraw
    ]);
    const drawBoxes = useCallback((param)=>{
        let { boxes, canvas } = param;
        if (boxes.length === 0) {
            return;
        }
        drawClearedBoundingBoxOnCanvas({
            canvas,
            box: boxes[0],
            strokeColor,
            borderWidth: BORDER_WIDTH,
            isEditable: !!newAnnotation
        });
    }, [
        newAnnotation,
        strokeColor
    ]);
    const { maxY, maxX, minX } = useMemo(()=>newAnnotation ? getAnnotationCommentPos(newAnnotation) : {
            maxX: 0,
            maxY: 0,
            minX: 0
        }, [
        newAnnotation
    ]);
    const onBoundingBoxClear = useCallback(()=>{
        setQueryParams({
            [QueryParamNames.discussionId]: null
        }, "replace");
        onClear();
    }, [
        onClear
    ]);
    const onBoundingBoxDrawn = useCallback((box)=>{
        const createdAnnotation = {
            dimensions: {
                height: box.pctEndY,
                width: box.pctEndX
            },
            coordinates: {
                x: box.pctStartX,
                y: box.pctStartY
            },
            type: "box",
            color: strokeColor,
            pageNumber
        };
        onAnnotationDrawn(createdAnnotation);
    }, [
        onAnnotationDrawn,
        pageNumber,
        strokeColor
    ]);
    const annotationPageNumber = getPageNumber(annotationToDraw);
    const boundingBoxToDraw = useMemo(()=>annotationBoxToDraw && (annotationPageNumber === pageNumber || isUndefined(annotationPageNumber)) ? [
            annotationBoxToDraw
        ] : [], [
        annotationBoxToDraw,
        annotationPageNumber,
        pageNumber
    ]);
    const hasActiveAnnotation = !!activeAnnotation;
    const onMouseDown = useCallback((param)=>{
        let { event } = param;
        event.stopPropagation();
        if (hasActiveAnnotation) {
            onBoundingBoxClear();
            return true;
        }
        return false;
    }, [
        hasActiveAnnotation,
        onBoundingBoxClear
    ]);
    if (!containerWidth || !containerHeight) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BoundingBoxLayer, {
                editableBoundingBoxIndex: !!newAnnotation ? 0 : undefined,
                onMouseDown: onMouseDown,
                drawNewBox: drawNewAnnotation,
                drawBoxes: drawBoxes,
                hasCustomCursor: true,
                disabled: disabled,
                shouldDrawOverlay: shouldDrawOverlay,
                containerHeight: containerHeight,
                containerWidth: containerWidth,
                containerLeft: containerLeft,
                containerTop: containerTop,
                boundingBoxes: boundingBoxToDraw,
                onClear: onBoundingBoxClear,
                onIsSelectingChange: _onIsSelectingChange,
                onBoundingBoxDrawn: onBoundingBoxDrawn
            }),
            !isDrawingActive && newAnnotation && getPageNumber(newAnnotation) === pageNumber && /*#__PURE__*/ _jsx(AnnotationCommentInput, {
                containerRef: containerRef,
                containerWidth: containerWidth,
                containerHeight: containerHeight,
                startX: minX,
                endX: maxX,
                endY: maxY,
                containerLeft: containerLeft
            })
        ]
    });
});
BoxAnnotationLayer.displayName = "BoxAnnotationLayer";
